// DEV_PATH:
// http://localhost:5173/dashboard/energy-audit/current/?/energy-passport/?

import clsx from "clsx"
import type { PropsWithChildren } from "react"
import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { Outlet, useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom"
import { useFetchAuditRequestById } from "@/features/audit-request/api/use-fetch-audit-request-by-id.ts"
import { useFetchContractByRequestId } from "@/features/energy-audit/api/audit-contracts/use-fetch-contract-by-request-id.ts"
import type { TAB_PAGES } from "@/features/map/constants.ts"
import { Breadcrumb, PageWrapper, Tab, type TabItem } from "@/shared/components"
import { useCurrentUser } from "@/shared/hooks/use-current-user.ts"
import { getLastPath } from "@/shared/utils"

const EnergyPassportWrapper = ({ children }: PropsWithChildren) => {
  // Helpers
  const navigate = useNavigate()
  const { isCompany } = useCurrentUser()
  const { pathname, search } = useLocation()
  const { id, contractId } = useParams()
  const { t } = useTranslation()
  const [searchParams] = useSearchParams()
  const requestId = searchParams.get("requestId")

  // States
  const [activeTab, setActiveTab] = useState<TabItem>()

  const last = getLastPath(pathname)
  const linkArr = pathname.replace(/\/+$/, "").split("/")

  // Queries
  const { data: audit, isPending: isPendingEnergyAudit } = useFetchAuditRequestById(requestId || undefined)
  const { data: contractData } = useFetchContractByRequestId(requestId || "")

  const endYear = new Date(audit?.created_at || new Date()).getFullYear()
  const lastFiveYears = Array.from({ length: 3 }, (_, i) => endYear - i - 1).reverse() || []

  const TABS: Record<string, TabItem> = Object.fromEntries(
    lastFiveYears.map(year => [
      year.toString(),
      {
        label: year.toString(),
        path: year.toString()
      }
    ])
  )

  // Effects
  useEffect(() => {
    if (last in TABS) {
      setActiveTab(TABS[last])
    } else {
      linkArr.map(link => {
        if (link in TABS) {
          setActiveTab(TABS[link as TAB_PAGES])
          return
        }
      })
    }
  }, [pathname])

  const onTabChange = (newTab: TabItem) => {
    setActiveTab(newTab)
    const year = newTab.path
    if (isCompany) {
      navigate(`/dashboard/company-energy-audit/current/${id}/energy-passport/${contractId}/${year}?${searchParams.toString()}`)
    } else {
      navigate(`${year}${search}`)
    }
  }

  return (
    <PageWrapper>
      <Breadcrumb />
      <h1 className="font-semibold text-2xl">{contractData?.company?.name}</h1>
      <div className="!h-16">
        {isPendingEnergyAudit ? (
          <Tab
            size="sm"
            tabs={Object.values(TABS)}
            renderTab={tab => (
              <div className="flex animate-pulse select-none items-center gap-2 rounded-md bg-zinc-200 text-transparent backdrop-blur duration-700">
                <span className={clsx("h-2.5 w-2.5 rounded-full ", tab.path === activeTab?.path ? "bg-blue-600" : "bg-DarkGray")} />
                {t(tab.label)}
              </div>
            )}
            value={activeTab}
            onChange={onTabChange}
          />
        ) : (
          <Tab
            size="sm"
            tabs={Object.values(TABS)}
            renderTab={tab => (
              <div className="flex select-none items-center gap-2">
                <span className={clsx("h-2.5 w-2.5 rounded-full", tab.path === activeTab?.path ? "bg-blue-600" : "bg-DarkGray")} />
                {t(tab.label)}
              </div>
            )}
            value={activeTab}
            onChange={onTabChange}
          />
        )}
      </div>
      <div className="flex h-full flex-col gap-4 overflow-hidden">{children || <Outlet />}</div>
    </PageWrapper>
  )
}

export default EnergyPassportWrapper
